<template>
	<div
		:id="_uid"
		ref="container"
		class="progress-bar"
		:tabindex="tabIndex"
		role="progressbar"
		:aria-describedby="_uid"
		:aria-valuetext="`${filledRatio} %`"
		aria-valuemin="0"
		:aria-valuenow="filledRatio"
		aria-valuemax="100"
		@keyup.esc="reportRatio"
		@keydown.enter="reportRatio"
		@keydown.space="reportRatio"
	>
		<div
			ref="empty"
			class="progress-bar__empty"
			:class="backgroundClass"
			:style="backgroundStyle"
		/>

		<div
			ref="filled"
			class="progress-bar__filled"
			:class="foregroundClass"
			:style="foregroundStyle"
		>
			<span
				class="progress-bar__tooptip-anchor"
				:data-anchor="filledAnchor"
			/>
		</div>

		<div
			v-if="accessibilityLabel"
			class="screen-reader-text"
		>
			<p :id="_uid">
				{{ accessibilityLabel }}
			</p>
		</div>
	</div>
</template>

<script>
export default {
	name: 'ProgressBar',

	props: {
		accessibilityLabel: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the label that will be read by screen readers',
		},

		background: {
			type: String,
			required: false,
			default: 'default',
			validator: (value) => {
				const supportedState = ['alert', 'danger', 'default', 'info', 'success', 'primary'];
				const hexadecimalRegex = /^#([0-9A-F]{3}){1,2}$/i;
				return supportedState.indexOf(value) !== -1 || hexadecimalRegex.test(value);
			},
			description: 'Sets the background colour of the ProgressBar, supports either a state or an hexadecimal value',
		},

		color: {
			type: String,
			required: false,
			default: 'default',
			validator: (value) => {
				const supportedState = ['alert', 'danger', 'default', 'info', 'success', 'primary'];
				const hexadecimalRegex = /^#([0-9A-F]{3}){1,2}$/i;
				return supportedState.indexOf(value) !== -1 || hexadecimalRegex.test(value);
			},
			description: 'Sets the foreground colour of the ProgressBar, supports either a state or an hexadecimal value',
		},

		duration: {
			type: Number,
			required: false,
			default: 0,
			validator: duration => duration >= 0,
			description: 'Sets the duration of the ProgressBar\'s animation',
		},

		filledAnchor: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the name of the anchor to which the external tooltip will attach itself',
		},

		fullWidth: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the ProgressBar will be the full width of its container',
		},

		height: {
			type: Number,
			required: false,
			default: 5,
			validator: height => height >= 0,
			description: 'Sets the height of the ProgressBar',
		},

		total: {
			type: Number,
			required: false,
			default: 100,
			validator: total => total >= 0,
			description: 'Sets the denominator of the ProgressBar fraction',
		},

		value: {
			type: Number,
			required: true,
			validator: value => value >= 0,
			description: 'Sets the numerator of the ProgressBar fraction',
		},

		width: {
			type: Number,
			required: false,
			default: 50,
			validator: width => width >= 0,
			description: 'Sets the width of the ProgressBar',
		},
	},

	data() {
		return {
			filledRatio: 0,
			tabIndex: -1,
			supportedState: ['alert', 'danger', 'default', 'info', 'success', 'primary'],
		};
	},

	computed: {
		foregroundClass() {
			return this.isValidState(this.color) ? `progress-bar--${this.color}` : '';
		},

		foregroundStyle() {
			const style = {
				width: `${this.filledRatio}%`,
				transition: `${this.duration}ms linear`,
			};

			if (!this.isValidState(this.color)) {
				style.backgroundColor = this.color;
			}

			return style;
		},

		backgroundClass() {
			return this.isValidState(this.background) ? `progress-bar--${this.background}` : '';
		},

		backgroundStyle() {
			if (!this.isValidState(this.background)) {
				return {
					backgroundColor: this.background,
				};
			}

			return null;
		},
	},

	watch: {
		value() {
			this.updateFilledRatio();
		},
	},

	mounted() {
		this.$refs.container.style.width = this.fullWidth ? '100%' : `${this.width}px`;
		this.$refs.container.style.height = `${this.height}px`;
		this.updateFilledRatio();
	},

	methods: {
		updateFilledRatio() {
			this.$nextTick(() => {
				this.filledRatio = (this.value * 100) / this.total;
				if (this.filledRatio > 0) {
					this.tabIndex = 0;
				}
			});
		},

		isValidState(state) {
			return this.supportedState.indexOf(state) !== -1;
		},
	},
};
</script>

<style scoped lang="scss" src="./progress-bar.scss" />
